/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  ManagedHeader,
  ManagedStatusTag,
  PRODUCT_NAMES,
  ReorderState,
  useManagedInstances,
} from '../../components/managed/managedShared';
import { formatMoney } from '../../components/managed/OrderServiceModal';
import { Page, Card, Button, Chip, LinkButton, TableCard, EmptyState, Skeleton } from '../../components/console/ui';
import type { ManagedServiceInstance, ManagedServiceStatus } from '../../types/managedServices';

// Re-exported for ManagedInstanceDetail and any other existing importers.
export { formatMoney };
export const StatusBadge = ManagedStatusTag;

type Filter = 'all' | ManagedServiceStatus;

const FILTERS: { value: Filter; label: string; always?: boolean }[] = [
  { value: 'all', label: 'All', always: true },
  { value: 'active', label: 'Active', always: true },
  { value: 'provisioning', label: 'Provisioning', always: true },
  { value: 'suspended', label: 'Suspended' },
  { value: 'failed', label: 'Failed' },
  { value: 'deleting', label: 'Deleting' },
  { value: 'deleted', label: 'Deleted', always: true },
];

const ManagedInstances: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useManagedInstances();
  const instances = useMemo(() => data || [], [data]);
  const [filter, setFilter] = useState<Filter>('all');

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: instances.length };
    instances.forEach((i) => {
      c[i.status] = (c[i.status] || 0) + 1;
    });
    return c;
  }, [instances]);

  const live = instances.filter((i) => i.status !== 'deleted');
  const deleted = instances.filter((i) => i.status === 'deleted');
  const rows = filter === 'all' ? instances : instances.filter((i) => i.status === filter);

  const reorder = (i: ManagedServiceInstance) => {
    const state: ReorderState = {
      reorder: { product: i.product, tier: i.tier, storage_gb: i.storage_gb, name: i.name },
    };
    navigate('/managed-mlops', { state });
  };

  return (
    <Page>
      <ManagedHeader actions={<LinkButton to="/managed-mlops">Order a service</LinkButton>} />

      {isLoading ? (
        <Card className="p-4 flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </Card>
      ) : isError ? (
        <EmptyState
          title="Couldn’t load instances"
          body="Failed to load managed instances."
          actions={<Button onClick={() => refetch()}>Retry</Button>}
        />
      ) : instances.length === 0 ? (
        <EmptyState
          title="No managed instances yet"
          body="Order a fully-managed MLOps service from the catalog to see it here."
          actions={<LinkButton to="/managed-mlops">Browse catalog</LinkButton>}
        />
      ) : (
        <>
          {live.length === 0 && (
            <Card as="section" aria-labelledby="no-active" className="p-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-col gap-1.5 max-w-[640px]">
                <h2 id="no-active" className="m-0 text-lg font-medium">
                  No active instances
                </h2>
                <p className="m-0 text-sm text-ps-muted leading-relaxed">
                  {deleted.length > 0
                    ? `${deleted.length === 1 ? 'Your instance was' : `All ${deleted.length} of your instances were`} deleted. `
                    : ''}
                  Re-order one to get a fresh endpoint and web UI; billing restarts monthly from the order date.
                </p>
              </div>
              <LinkButton to="/managed-mlops" variant="secondary">
                Browse catalog
              </LinkButton>
            </Card>
          )}

          <div className="flex flex-wrap gap-2 items-center">
            {FILTERS.filter((f) => f.always || counts[f.value]).map((f) => (
              <Chip key={f.value} on={filter === f.value} onClick={() => setFilter(f.value)}>
                {f.label} <span className="text-ps-subtle">{counts[f.value] || 0}</span>
              </Chip>
            ))}
          </div>

          <TableCard>
            <thead>
              <tr>
                <th>Name</th>
                <th>Service</th>
                <th>Tier</th>
                <th>Storage</th>
                <th>Status</th>
                <th>Monthly</th>
                <th>Next renewal</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-ps-muted py-8">
                    No instances with this status.
                  </td>
                </tr>
              ) : (
                rows.map((instance) => {
                  const total = instance.monthly_cost + instance.storage_monthly_cost;
                  return (
                    <tr key={instance.id}>
                      <td>
                        <Link
                          to={`/managed-mlops/instances/${instance.id}`}
                          className="text-ps-text font-medium hover:text-ps-accent"
                        >
                          {instance.name}
                        </Link>
                      </td>
                      <td>{PRODUCT_NAMES[instance.product] || instance.product}</td>
                      <td className="capitalize">{instance.tier}</td>
                      <td>{instance.storage_gb} GB</td>
                      <td>
                        <ManagedStatusTag status={instance.status} failureReason={instance.failure_reason} />
                        {instance.status === 'failed' && instance.failure_reason && (
                          <p className="m-0 text-xs text-ps-err/80 mt-1 max-w-xs truncate" title={instance.failure_reason}>
                            {instance.failure_reason}
                          </p>
                        )}
                      </td>
                      <td>{formatMoney(total, instance.currency)}</td>
                      <td className="text-ps-muted">
                        {instance.next_renewal_at ? format(new Date(instance.next_renewal_at), 'd MMM yyyy') : '-'}
                      </td>
                      <td>
                        <div className="flex justify-end gap-2">
                          {instance.status === 'active' && instance.endpoint_url && (
                            <LinkButton to={instance.endpoint_url} external size="sm">
                              Open web UI
                            </LinkButton>
                          )}
                          {instance.status === 'deleted' ? (
                            <Button variant="secondary" size="sm" onClick={() => reorder(instance)}>
                              Re-order
                            </Button>
                          ) : (
                            <LinkButton to={`/managed-mlops/instances/${instance.id}`} variant="secondary" size="sm">
                              Details
                            </LinkButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </TableCard>
        </>
      )}
    </Page>
  );
};

export default ManagedInstances;
